import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { AppreciationStaleBanner } from "@/features/journal/components/AppreciationStaleBanner";
import { TrendingDown } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { type DepreciationStateFilter, useAccountDepreciation } from "../api/depreciationApi";
import { DepreciationList } from "../components/DepreciationList";
import { DepreciationSummary } from "../components/DepreciationSummary";
import { MonthSelector, currentYearMonth } from "../components/MonthSelector";

const VALID_STATE_FILTERS = ["all", "active", "terminated"] as const;

function parseStateParam(raw: string | null): DepreciationStateFilter {
  if (raw && (VALID_STATE_FILTERS as readonly string[]).includes(raw)) {
    return raw as DepreciationStateFilter;
  }
  return "all";
}

function parseMonthParam(raw: string | null): string {
  // YYYY-MM, fallback al mes actual del cliente. Si la URL trae un valor inválido
  // (e.g. `?month=evil`), descartamos en vez de pasarlo al backend.
  if (raw && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) return raw;
  return currentYearMonth();
}

export function DepreciacionPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const canRecompute = currentMembership?.role === "owner" || currentMembership?.role === "admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const month = parseMonthParam(searchParams.get("month"));
  const stateFilter = parseStateParam(searchParams.get("state"));

  function setMonth(next: string) {
    const params = new URLSearchParams(searchParams);
    params.set("month", next);
    setSearchParams(params, { replace: true });
  }

  function setStateFilter(next: DepreciationStateFilter) {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("state");
    else params.set("state", next);
    setSearchParams(params, { replace: true });
  }

  // ──── Paginación V1: una sola página de hasta 200. Si excede,
  // mostramos un indicador "+ N más" y se aborda en V2 con cursor real.
  const [pageSize] = useState(200);

  const query = useAccountDepreciation(accountId, {
    month,
    state: stateFilter,
    limit: pageSize,
  });

  const items = query.data?.items ?? [];

  return (
    <>
      <title>Depreciación · HATO</title>

      <div className="flex flex-col gap-1">
        <h1 className="font-medium text-app-ink-1 text-xl tracking-tight">Depreciación</h1>
        <p className="text-[13px] text-app-ink-3">
          Animales productivos y su depreciación contable a la fecha de corte.
        </p>
      </div>

      <AppreciationStaleBanner accountId={accountId} canRecompute={canRecompute} className="mt-4" />

      {/* Filtros */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-1.5">
          <label className="font-medium text-[12px] text-app-ink-3" htmlFor="depreciation-month">
            Mes
          </label>
          <MonthSelector value={month} onChange={setMonth} />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-medium text-[12px] text-app-ink-3" htmlFor="depreciation-state">
            Estado
          </label>
          <Select
            value={stateFilter}
            onValueChange={(v) => setStateFilter(v as DepreciationStateFilter)}
          >
            <SelectTrigger
              id="depreciation-state"
              data-testid="depreciation-state-filter"
              className="h-10 w-[180px] text-base"
              aria-label="Filtrar por estado"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="terminated">Terminados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <DepreciationSummary
          monthDepreciation={query.data?.totalMonthDepreciation ?? "0.00"}
          accumulatedDepreciation={query.data?.totalAccumulatedDepreciation ?? "0.00"}
          remainingBookValue={query.data?.totalRemainingBookValue ?? "0.00"}
          isLoading={query.isLoading}
        />
      </div>

      <div className="mt-4">
        {query.isLoading ? (
          <ListSkeleton />
        ) : query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <DepreciationList items={items} />
            {query.data?.nextCursor !== null && query.data?.nextCursor !== undefined && (
              <p
                className="mt-3 text-center text-[12px] text-app-ink-3"
                data-testid="depreciation-truncated-notice"
              >
                Mostrando los primeros {items.length} animales. La paginación completa llega en una
                próxima versión.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={i}
          className="h-16 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <p className="text-app-ink-2 text-sm">No se pudo cargar la depreciación.</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
      >
        Reintentar
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-16 text-center"
      data-testid="depreciation-empty-state"
    >
      <div className="grid h-12 w-12 place-items-center rounded-full bg-app-surface-2 text-app-ink-2">
        <TrendingDown size={20} />
      </div>
      <div>
        <h2 className="font-medium text-app-ink-1 text-xl tracking-tight">
          Aún no hay animales en producción este mes
        </h2>
        <p className="mt-1 max-w-sm text-app-ink-3 text-sm">
          La depreciación arranca desde el primer parto. Cuando una vaquilla cruza esa frontera, su
          schedule contable empieza a generarse.
        </p>
      </div>
    </div>
  );
}
